/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldCheck, Eye, EyeOff, Lock } from 'lucide-react';

/**
 * Phase 326e — Forced First-Login Password Reset Modal
 * ----------------------------------------------------
 * Renders a NON-DISMISSIBLE full-screen modal whenever the logged-in
 * user has `password_must_reset === true` on their session (set when
 * the WhatsApp bot or the manual /api/students POST issues a temp
 * password like `Kar24@123`).
 *
 *  • Pre-fills the `current_password` from sessionStorage when the
 *    AuthContext stashed it on login (so the parent doesn't have to
 *    re-type the WhatsApp-issued temp password — they just confirm).
 *  • Requires the new password to be ≥ 8 chars and DIFFERENT from the
 *    current temp.
 *  • Calls `POST /api/auth/change-password-first-login`. On success,
 *    swaps the JWT, clears the flag from local state, clears the
 *    sessionStorage stash, and shows a "Welcome to Out-Play" toast.
 *
 *  The modal mounts at the root (App.js) so it covers EVERY page —
 *  including settings, profile, etc. There is no close button.
 */
export default function FirstLoginResetModal() {
  const { user, setUser, setToken } = useAuth();
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const mustReset = !!(user && user.password_must_reset);

  // Prefill the current_password from sessionStorage when AuthContext stashed
  // it at login. Falls back to empty so the parent can type it in manually.
  useEffect(() => {
    if (!mustReset) return;
    try {
      const stashed = sessionStorage.getItem('outplay_temp_pw');
      if (stashed) setCurrentPwd(stashed);
    } catch (_) { /* ignore */ }
  }, [mustReset]);

  const validate = useCallback(() => {
    if (newPwd.length < 8) return 'New password must be at least 8 characters.';
    if (newPwd === currentPwd) return 'New password must be different from your temporary password.';
    if (newPwd !== confirmPwd) return 'New passwords do not match.';
    return '';
  }, [newPwd, currentPwd, confirmPwd]);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    const v = validate();
    if (v) { setError(v); return; }
    setBusy(true);
    setError('');
    try {
      const res = await api.post('/auth/change-password-first-login', {
        current_password: currentPwd,
        new_password: newPwd,
      });
      const { access_token } = res.data || {};
      if (access_token) {
        localStorage.setItem('outplay_token', access_token);
        setToken(access_token);
      }
      // Clear the must_reset flag locally so the modal unmounts.
      const next = { ...(user || {}), password_must_reset: false };
      setUser(next);
      localStorage.setItem('outplay_user', JSON.stringify(next));
      try { sessionStorage.removeItem('outplay_temp_pw'); } catch (_) { /* ignore */ }
      toast.success('Password updated — Welcome to Out-Play!');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not update your password. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [currentPwd, newPwd, validate, setToken, setUser, user]);

  if (!mustReset) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="first-login-reset-modal"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden border-2 border-emerald-200">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-700 to-teal-700 p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-xl bg-white/20 ring-1 ring-white/40 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold leading-tight" data-testid="first-login-reset-title">
                Set Your Permanent Password
              </h2>
              <p className="text-emerald-50/90 text-xs mt-0.5">
                One-time setup before you can use Out-Play
              </p>
            </div>
          </div>
          <p className="text-emerald-50/95 text-sm leading-relaxed">
            You're logged in with a temporary password from WhatsApp. Please pick
            a permanent password to keep your account secure.
          </p>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
              <Lock className="h-3 w-3" /> Temporary password
            </label>
            <Input
              type="password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              placeholder="The password from your WhatsApp message"
              className="font-mono text-sm"
              autoComplete="current-password"
              data-testid="first-login-reset-current-password"
              disabled={busy}
              required
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
              <Lock className="h-3 w-3" /> New password
            </label>
            <div className="relative">
              <Input
                type={showNew ? 'text' : 'password'}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="At least 8 characters"
                className="font-mono text-sm pr-10"
                autoComplete="new-password"
                data-testid="first-login-reset-new-password"
                disabled={busy}
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
              <Lock className="h-3 w-3" /> Confirm new password
            </label>
            <Input
              type={showNew ? 'text' : 'password'}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              placeholder="Re-enter the new password"
              className="font-mono text-sm"
              autoComplete="new-password"
              data-testid="first-login-reset-confirm-password"
              disabled={busy}
              required
              minLength={8}
            />
          </div>

          {error && (
            <div
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
              data-testid="first-login-reset-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy || !currentPwd || !newPwd || !confirmPwd}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white h-11"
            data-testid="first-login-reset-submit"
          >
            {busy ? 'Updating…' : 'Set Password & Continue'}
          </Button>

          <p className="text-[11px] text-center text-slate-500 mt-2">
            For your security, you cannot use Out-Play until you set a permanent password.
          </p>
        </form>
      </div>
    </div>
  );
}
